import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, delay, map, of, switchMap, tap } from 'rxjs';

import { MazStatus } from '@ninety/platform/models/maz/maz-status.enum';
import { CurrentPersonStateActions } from '@ninety/platform/state';

import { GuideActions } from '../../guide/_state/guide.actions';
import { MazChatService } from '../services/maz-chat.service';

import { MazActions } from './maz.actions';

@Injectable()
export class MazEffects {
  private actions$ = inject(Actions);
  private mazChatService = inject(MazChatService);

  getConversationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuideActions.init),
      switchMap(() =>
        this.mazChatService
          .getConversationHistory()
          .pipe(map(mazMessages => MazActions.getConversationHistorySuccess({ mazMessages })))
      )
    )
  );

  sendMessageToMaz$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazActions.sendMessageToMaz),
      switchMap(({ userInput }) =>
        this.mazChatService.sendMazMessage(userInput).pipe(
          map(response => MazActions.receiveMessageFromMaz({ response })),
          catchError((error: unknown) =>
            of(
              MazActions.failedToReceiveMessageFromMaz({
                error,
                message: 'Failed to receive response from Maz',
              })
            )
          )
        )
      )
    )
  );

  sendMessageFeedback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MazActions.sendMessageFeedback),
        switchMap(({ feedback }) => this.mazChatService.submitFeedback(feedback))
      ),
    { dispatch: false }
  );

  showWaitingForReply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazActions.sendMessageToMaz),
      delay(1000),
      map(() => MazActions.waitingForMazReply())
    )
  );

  keepScrollOnBottom$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MazActions.sendMessageToMaz, MazActions.waitingForMazReply, MazActions.sendMessageFeedback),
        tap(() => {
          setTimeout(() => {
            const conversation = document.getElementById('maz-conversation');
            conversation.scrollTop = conversation.scrollHeight;
          });
        })
      ),
    { dispatch: false }
  );

  scrollToTopOfLastMazMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MazActions.receiveMessageFromMaz),
        tap(() => {
          setTimeout(() => {
            const conversation = document.getElementById('maz-conversation');
            const messages = document.getElementsByClassName('maz-message');
            const lastMessagePosition = (messages[messages.length - 1] as HTMLElement)?.offsetTop;
            conversation.scrollTop = lastMessagePosition - 100;
          });
        })
      ),
    { dispatch: false }
  );

  toggleUserMazChatStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazActions.toggleUserMazStatus),
      map(({ enabled }) => {
        const mazStatus = enabled ? MazStatus.ENABLED : MazStatus.DISABLED;
        return CurrentPersonStateActions.updateCurrentPerson({ update: { mazStatus } });
      })
    )
  );
}
